import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { HttpHelpService } from 'src/app/Data/service/http-help.service';
import { NavService } from '../../../../services/nav.service';
import { LanguageService } from 'src/app/shared/services/language.service';

interface LanguageSetting {
	language: string;
	code: 'en' | 'ar' | 'he';
	icon: string;
}

@Component({
	selector: 'app-languages',
	templateUrl: './languages.component.html',
	styleUrls: ['./languages.component.scss']
})
export class LanguagesComponent implements OnInit {

	public languages: LanguageSetting[] = [
		{ language: 'English', code: 'en', icon: 'us' },
		{ language: 'Arabic', code: 'ar', icon: 'sa' },
		{ language: 'Hebrew', code: 'he', icon: 'il' }
	];

	// Default to English, will be updated in ngOnInit
	public selectedLanguage: LanguageSetting = this.languages[0];
	private userRegistId: string | number | null = null;

	constructor(
		public navServices: NavService, // Assumed to be used in the template
		private translate: TranslateService,
		private spinner: NgxSpinnerService,
		private service: HttpHelpService,
		private languageService: LanguageService,
	) {
		const userIdItem = localStorage.getItem('user_id');
		if (userIdItem) {
			try {
				this.userRegistId = JSON.parse(userIdItem);
			} catch (error) {
				console.error('Failed to parse user_id from localStorage:', error);
				// userRegistId remains null or handle as appropriate
			}
		}
	}

	ngOnInit(): void {
		const storedLangCode = localStorage.getItem('curentLang');
		const initialLangCodeIsValid = this.languages.some(lang => lang.code === storedLangCode);
		
		const initialLangCode: LanguageSetting['code'] = initialLangCodeIsValid
			? storedLangCode as LanguageSetting['code']
			: 'en';
			
		this.applyLanguageSettings(initialLangCode, false);
	}

	/**
	 * Handles the language change initiated by user interaction.
	 * Updates the language on the backend and then applies the change on the client side.
	 * @param langCode The code of the language to switch to.
	 */
	public changeLang(langCode: LanguageSetting['code']): void {
		if (this.selectedLanguage.code === langCode) {
			return; // Avoid API call if language is already selected
		}

		this.spinner.show();

		const model = {
			language: langCode,
			user_id: this.userRegistId
		};

		this.service.updateUserLangById(model).subscribe({
			next: res => {
				if (res && res.status === 200) {
					this.applyLanguageSettings(langCode, true);
				}
				// Consider else case for res.status !== 200 if needed
				this.spinner.hide();
			},
			error: () => { // Add type for err if available: err: any
				// console.error('Error updating language:', err); // Optional: for debugging
				this.spinner.hide();
				// Optionally, notify the user of the error
			}
		});
	}

	/**
	 * Applies language changes to the client side:
	 * - Stores the language code in localStorage.
	 * - Sets the language for ngx-translate.
	 * - Updates the selectedLanguage property.
	 * - Sets the document direction (LTR/RTL).
	 * @param langCode The code of the language to apply.
	 */
	private applyLanguageSettings(langCode: LanguageSetting['code'], refreshRoute: boolean): void {
		this.languageService.apply(langCode, { refreshRoute });

		const newSelectedLanguage = this.languages.find(language => language.code === langCode);
		if (newSelectedLanguage) {
			this.selectedLanguage = newSelectedLanguage;
		}
	}
}
